import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Formik, Form, Field } from 'formik'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import _ from 'lodash'

import Modal from '../components/Modal'
import DeleteButton from '../components/DeleteButton'
import { IconEdit } from '../components/icons'
import { DatePicker } from '../components/input/DatePicker'
import { SuggestedInput } from '../components/input/SuggestedInput'
import { colors, supportedCurrencies } from '../data'
import { useLocalState } from '../settings/localStorage'
import { useFxContext } from '../forecast/FxProvider'
import { FxConverter } from '../forecast/fx'
import { useSingleTeamMangement } from '../Management'
import IpManagementBreadcrumbs from './IpManagementBreadcrumbs'
import { useRunningCosts, RunningCost } from './RunningCostProvider'

type CostSource = 'internal' | 'external'

export interface RunningEntry {
    tag: string
    vendor: string
    source: CostSource
    date: string
    amount: string
    currency: string
    vat: string
    comment: string
}

const defaultTags = [
    'IP management',
    'Patent attorney cost',
    'Patent attorney support',
    'IP management tool',
    'Search tool',
    'Monitoring tool',
]

const YEARS_AHEAD = 6

function runningCostToEntry(c: RunningCost): RunningEntry {
    return {
        tag: c.tag,
        vendor: c.vendor,
        source: c.source,
        date: c.date,
        amount: String(c.amount),
        currency: c.currency,
        vat: String(c.vat),
        comment: c.comment,
    }
}

function entryToRunningCost(e: RunningEntry, existing?: RunningCost): RunningCost {
    return {
        ...(existing ?? {}),
        tag: e.tag,
        vendor: e.vendor,
        source: e.source,
        date: e.date,
        amount: parseFloat(e.amount) || 0,
        currency: e.currency,
        vat: parseFloat(e.vat) || 0,
        comment: e.comment,
    } as RunningCost
}

export default function Forecast() {
    const { runningCosts, postRunningCost, deleteRunningCost } = useRunningCosts()
    const [tags, setTags] = useLocalState('finances.running-cost-tags', defaultTags)
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const { fxConverter } = useFxContext()
    const { team } = useSingleTeamMangement()
    const targetCurrency = team?.currency ?? 'EUR'

    const entries = runningCosts.map(runningCostToEntry)

    function addTag(tag: string) {
        const trimmed = tag.trim()
        if (trimmed === '') return
        if (!tags.includes(trimmed)) setTags([...tags, trimmed])
    }

    async function handleSubmit(values: RunningEntry) {
        addTag(values.tag)
        const existing = editingIndex !== null && editingIndex >= 0 ? runningCosts[editingIndex] : undefined
        await postRunningCost(entryToRunningCost(values, existing))
        setEditingIndex(null)
    }

    async function deleteEntry(index: number) {
        await deleteRunningCost(runningCosts[index])
    }

    const graphData = projectCosts(entries, fxConverter, targetCurrency)

    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>Forecast | Patent Cockpit</title>
            </Helmet>
            <div className="portfolio-menu max-w-2xl items-baseline">
                <IpManagementBreadcrumbs label="Forecast" />
            </div>
            <div className="main-content bg-pcx-100 pt-2 flex flex-col gap-4 max-w-5xl">
                <div className="p-4 bg-white rounded-md shadow-sm max-w-full sm:max-w-xl">
                    <h3 className="mb-2">Cost forecast</h3>
                    <ForecastChart data={graphData} />
                    <div className="text-slate-500 text-sm w-full mt-2">
                        <div className="inline-flex gap-2 items-center"><div className="h-4 w-4 bg-pcx-500" />Costs ({targetCurrency})</div>
                    </div>
                </div>

                <div className="p-4 bg-white rounded-md shadow">
                    <div className="flex flex-row justify-between items-baseline mb-3">
                        <h3>Costs ({entries.length})</h3>
                        <button type="button" className="btn-primary" onClick={() => setEditingIndex(-1)}>
                            + Add cost
                        </button>
                    </div>
                    {entries.length === 0
                        ? <p className="text-slate-500 text-sm">No costs yet. Click <em>Add cost</em> to start.</p>
                        : <EntriesTable entries={entries} onEdit={i => setEditingIndex(i)} onDelete={deleteEntry} />
                    }
                </div>
            </div>

            {editingIndex !== null && (
                <Modal blurClick={() => setEditingIndex(null)} escAction={() => setEditingIndex(null)}>
                    <RunningForm
                        initialValues={editingIndex >= 0 ? entries[editingIndex] : undefined}
                        isEdit={editingIndex >= 0}
                        tags={_.union(defaultTags, tags)}
                        onSubmit={handleSubmit}
                        onCancel={() => setEditingIndex(null)}
                    />
                </Modal>
            )}
        </>
    )
}

export function projectCosts(entries: RunningEntry[], fxConverter: FxConverter, targetCurrency: string): { year: number, amount: number }[] {
    const currentYear = new Date().getFullYear()
    const years = _.range(currentYear, currentYear + YEARS_AHEAD)

    function contribution(e: RunningEntry, year: number) {
        const amount = parseFloat(e.amount) || 0
        const vat = parseFloat(e.vat) || 0
        const totalNative = amount + vat
        const converted = fxConverter.convert(totalNative, e.currency || targetCurrency, targetCurrency)
        const total = converted ?? totalNative
        const entryYear = e.date ? new Date(e.date).getFullYear() : year
        return entryYear === year ? total : 0
    }

    return years.map(year => ({
        year,
        amount: _.sumBy(entries, e => contribution(e, year)),
    }))
}

export function ForecastChart({ data, yAxisWidth }: { data: { year: number, amount: number }[], yAxisWidth?: number }) {
    const format = new Intl.NumberFormat()
    return (
        <ResponsiveContainer className="text-sm" width="100%" height={220}>
            <BarChart data={data}>
                <XAxis dataKey="year" />
                <YAxis tickFormatter={v => format.format(v)} width={yAxisWidth} />
                <Bar name="Costs" dataKey="amount" fill={colors['pcx-500']} />
                <Tooltip
                    cursor={false}
                    wrapperStyle={{ outline: 'none' }}
                    formatter={v => format.format(v as number)}
                />
            </BarChart>
        </ResponsiveContainer>
    )
}

function EntriesTable({ entries, onEdit, onDelete }: {
    entries: RunningEntry[],
    onEdit: (index: number) => void,
    onDelete: (index: number) => void,
}) {
    const format = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return (
        <div className="overflow-x-auto">
            <table className="tabular-nums w-full">
                <thead>
                    <tr className="pc-header-row">
                        <th className="pc-header-cell"></th>
                        <th className="pc-header-cell">Cost type</th>
                        <th className="pc-header-cell">Vendor</th>
                        <th className="pc-header-cell">Date</th>
                        <th className="pc-header-cell text-right">Amount</th>
                        <th className="pc-header-cell text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map((e, i) => {
                        const total = (parseFloat(e.amount) || 0) + (parseFloat(e.vat) || 0)
                        return (
                            <tr key={i} className="border-b border-pcx-200 last:border-pcx-300">
                                <td className="pc-cell">
                                    <span
                                        title={e.source === 'internal' ? 'Internal' : 'External'}
                                        className="inline-block w-3 h-3 rounded-full"
                                        style={{ backgroundColor: e.source === 'internal' ? colors['pcx-500'] : colors['pcx-300'] }}
                                    />
                                </td>
                                <td className="pc-cell font-medium">{e.tag || '—'}</td>
                                <td className="pc-cell">{e.vendor || '—'}</td>
                                <td className="pc-cell text-sm text-slate-600">{e.date}</td>
                                <td className="pc-cell text-right">{format.format(total)} {e.currency}</td>
                                <td className="pc-cell">
                                    <div className="flex flex-row gap-2 justify-end">
                                        <button
                                            type="button"
                                            title="Edit"
                                            className="btn-secondary w-7 h-7 p-1"
                                            onClick={() => onEdit(i)}
                                        >
                                            <IconEdit />
                                        </button>
                                        <DeleteButton
                                            className="btn-warn-secondary w-7 h-7 p-1"
                                            deleteAction={() => onDelete(i)}
                                            question={`Delete cost "${e.tag || '(no tag)'}"?`}
                                        />
                                    </div>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function RunningForm({ initialValues, isEdit, tags, onSubmit, onCancel }: {
    initialValues?: RunningEntry,
    isEdit: boolean,
    tags: string[],
    onSubmit: (entry: RunningEntry) => void,
    onCancel: () => void,
}) {
    const defaults: RunningEntry = {
        tag: '',
        vendor: '',
        source: 'external',
        date: new Date().toISOString().slice(0, 10),
        amount: '0',
        currency: 'EUR',
        vat: '0',
        comment: '',
    }

    const seeded: RunningEntry = initialValues
        ? { ...initialValues, source: initialValues.source ?? 'external' }
        : defaults

    return (
        <Formik initialValues={seeded} onSubmit={onSubmit}>{({ values, setFieldValue }) =>
            <Form>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-[28rem]">
                    <h2 className="sm:col-span-2">{isEdit ? 'Edit cost' : 'Add cost'}</h2>

                    <div>
                        <label htmlFor="tag" className="label mb-1">Cost type</label>
                        <SuggestedInput
                            name="tag"
                            value={values.tag}
                            onChange={e => setFieldValue('tag', e.target.value)}
                            suggestions={tags}
                        />
                    </div>

                    <div>
                        <label htmlFor="date" className="label mb-1">Date</label>
                        <Field id="date" name="date" required className="form-input" as={DatePicker} />
                    </div>

                    <div>
                        <label htmlFor="vendor" className="label mb-1">Vendor</label>
                        <Field id="vendor" name="vendor" className="form-input" />
                    </div>

                    <div className="flex flex-row gap-4 max-sm:flex-wrap sm:col-span-2">
                        <div>
                            <label htmlFor="amount" className="label mb-1">Amount (excl. VAT)</label>
                            <Field id="amount" name="amount" type="number" required className="form-input w-36" />
                        </div>
                        <div>
                            <label htmlFor="currency" className="label mb-1">Currency</label>
                            <Field id="currency" name="currency" className="form-select" as="select">
                                {supportedCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                            </Field>
                        </div>
                        <div>
                            <label htmlFor="vat" className="label mb-1">VAT</label>
                            <Field id="vat" name="vat" type="number" className="form-input w-24" />
                        </div>
                        <div>
                            <div className="label mb-1">Total</div>
                            <div className="py-2 px-1 label">{add(values.amount, values.vat)} {values.currency}</div>
                        </div>
                    </div>

                    <div className="sm:col-span-2">
                        <label htmlFor="comment" className="label mb-1">Comment</label>
                        <Field id="comment" name="comment" as="textarea" className="w-full form-textarea h-24" />
                    </div>
                </div>
                <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
                    <input type="submit" className="btn-primary" value="Save" />
                    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
                </div>
            </Form>
        }</Formik>
    )
}

function add(a: string, b: string) {
    const an = parseFloat(a)
    const bn = parseFloat(b)
    return ((isNaN(an) ? 0.0 : an) + (isNaN(bn) ? 0.0 : bn)).toFixed(2)
}
